import { isPlatformBrowser } from '@angular/common';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';

/**
 * Applies the brand theme colors (CSS variables) and caches them for instant reload.
 * Generates color variants (100, 80, 60, 40) from base colors.
 */
@Injectable({
	providedIn: 'root',
})
export class BrandThemeService {
	private readonly platformId = inject(PLATFORM_ID);
	private readonly COLORS_CACHE_PREFIX = 'brand_colors_';

	constructor() {
		// Apply cached colors immediately on service initialization to prevent FOUC
		this.applyCachedColors();
	}

	/**
	 * Apply cached brand colors from localStorage synchronously.
	 * Uses the current hostname as cache key.
	 */
	applyCachedColors(): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		try {
			const cacheKey = this.getCacheKey();
			const cached = localStorage.getItem(cacheKey);
			if (cached) {
				const { primaryColor, secondaryColor } = JSON.parse(cached);
				this.applyColorsToRoot(primaryColor, secondaryColor);
			}
		} catch {
			// Ignore parsing errors
		}
	}

	/**
	 * Apply brand colors to CSS variables and cache them per host for instant reload.
	 * Generates lighter variants automatically from base colors.
	 */
	applyBrandColors(primaryColor: string | null, secondaryColor: string | null): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		this.applyColorsToRoot(primaryColor, secondaryColor);

		// Cache colors per host
		try {
			const cacheKey = this.getCacheKey();
			localStorage.setItem(cacheKey, JSON.stringify({ primaryColor, secondaryColor }));
		} catch {
			// Ignore storage errors
		}
	}

	/**
	 * Get cache key based on current hostname for color caching.
	 */
	private getCacheKey(): string {
		return this.COLORS_CACHE_PREFIX + window.location.host;
	}

	/**
	 * Apply color values to CSS custom properties on the document root.
	 */
	private applyColorsToRoot(primaryColor: string | null, secondaryColor: string | null): void {
		const root = document.documentElement;

		if (primaryColor) {
			const primaryVariants = this.generateColorVariants(primaryColor);
			root.style.setProperty('--primary-color-100', primaryVariants[100]);
			root.style.setProperty('--primary-color-80', primaryVariants[80]);
			root.style.setProperty('--primary-color-60', primaryVariants[60]);
			root.style.setProperty('--primary-color-40', primaryVariants[40]);
		}

		if (secondaryColor) {
			const secondaryVariants = this.generateColorVariants(secondaryColor);
			root.style.setProperty('--secondary-color-100', secondaryVariants[100]);
			root.style.setProperty('--secondary-color-80', secondaryVariants[80]);
			root.style.setProperty('--secondary-color-60', secondaryVariants[60]);
			root.style.setProperty('--secondary-color-40', secondaryVariants[40]);
		}
	}

	/**
	 * Reset colors to default (remove inline styles).
	 */
	resetColors(): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		localStorage.removeItem(this.getCacheKey());

		const root = document.documentElement;
		const colorVars = [
			'--primary-color-100',
			'--primary-color-80',
			'--primary-color-60',
			'--primary-color-40',
			'--secondary-color-100',
			'--secondary-color-80',
			'--secondary-color-60',
			'--secondary-color-40',
		];

		for (const varName of colorVars) {
			root.style.removeProperty(varName);
		}
	}

	/**
	 * Generate color variants from a base color.
	 * 100 = base color (darkest)
	 * 80 = 20% lighter
	 * 60 = 40% lighter
	 * 40 = 60% lighter
	 */
	private generateColorVariants(baseColor: string): Record<number, string> {
		const rgb = this.hexToRgb(baseColor);

		if (!rgb) {
			// If invalid color, return the same color for all variants
			return {
				100: baseColor,
				80: baseColor,
				60: baseColor,
				40: baseColor,
			};
		}

		return {
			100: baseColor,
			80: this.lightenColor(rgb, 0.2),
			60: this.lightenColor(rgb, 0.4),
			40: this.lightenColor(rgb, 0.6),
		};
	}

	/**
	 * Convert hex color to RGB object.
	 */
	private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
		// Remove # if present
		const cleanHex = hex.replace(/^#/, '');

		// Handle 3-digit hex
		const fullHex =
			cleanHex.length === 3
				? cleanHex
						.split('')
						.map((c) => c + c)
						.join('')
				: cleanHex;

		const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);

		if (!result) {
			return null;
		}

		return {
			r: parseInt(result[1], 16),
			g: parseInt(result[2], 16),
			b: parseInt(result[3], 16),
		};
	}

	/**
	 * Lighten a color by mixing with white.
	 * @param rgb RGB color object
	 * @param amount Amount to lighten (0-1, where 1 is white)
	 */
	private lightenColor(rgb: { r: number; g: number; b: number }, amount: number): string {
		const r = Math.round(rgb.r + (255 - rgb.r) * amount);
		const g = Math.round(rgb.g + (255 - rgb.g) * amount);
		const b = Math.round(rgb.b + (255 - rgb.b) * amount);

		return `#${this.componentToHex(r)}${this.componentToHex(g)}${this.componentToHex(b)}`;
	}

	/**
	 * Convert a number to 2-digit hex.
	 */
	private componentToHex(c: number): string {
		const hex = c.toString(16);
		return hex.length === 1 ? '0' + hex : hex;
	}
}
